import { CloseOutlined } from "@ant-design/icons";
import { useEffect, useRef, useMemo, useState, useCallback } from "react";
import { useDarkMode, useThemeColors } from "redux/hooks";
import { useViewModel } from "mvvm/View";
import { ViewModel } from "mvvm/ViewModel";
import { ConditionalTooltip } from "./ConditionalTooltip";
import dayjs from "dayjs";
import { ScheduleRegenModalView, ScheduleData } from "./ScheduleRegenModalView";
import { notification } from "antd";

// Interface for PageViewModel - compatible with both RunDash and RunNLI
interface IPageViewModel extends ViewModel {
    jobs: readonly {
        jobID: string;
        alias: string;
        question?: string;
        scheduleTime?: string;
        scheduleType?: string;
        replyTarget?: string;
    }[];
    selectedJob: any;
    jobService: {
        scheduleJob: (jobID: string, scheduleTime: string | null, scheduleType: string | null) => Promise<void>;
    };
    reloadJobs: () => Promise<void>;
}

export function ScheduleView(props: {
    isOpen: boolean;
    onClose: () => void;
    model: IPageViewModel;
    mode: 'dashboard' | 'stream';
}) {
    const cardRef = useRef<HTMLDivElement>(null);
    const darkMode = useDarkMode();
    const themeColors = useThemeColors();
    const model = useViewModel(props.model);
    const [editingJobID, setEditingJobID] = useState<string | null>(null);

    // Filter jobs that have a schedule
    const scheduledJobs = useMemo(() => {
        return model.jobs.filter(job => job.scheduleTime && job.scheduleType);
    }, [model.jobs]);

    // Function to get the root parent job alias (only used in stream mode)
    const getRootParentJobAlias = useCallback((job: typeof model.jobs[0]): string => {
        let currentJob = job;
        let parentID = currentJob.replyTarget ? currentJob.replyTarget.split('-').slice(1).join('-') : undefined;

        while (parentID) {
            const currentParentID = parentID; // Capture in closure-safe way
            const parentJob = model.jobs.find(j => j.jobID === currentParentID);
            if (!parentJob) {
                break;
            }
            currentJob = parentJob;
            parentID = parentJob.replyTarget ? parentJob.replyTarget.split('-').slice(1).join('-') : undefined;
        }

        return currentJob.alias;
    }, [model]);

    // Handle clicking outside the card
    const { isOpen, onClose } = props;
    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;

            // Don't close if the edit modal is open
            if (editingJobID) {
                return;
            }

            // Don't close if clicking on the Ant Design modal or its overlay
            if (target.closest('.ant-modal') ||
                target.closest('.ant-modal-mask') ||
                target.closest('.ant-modal-wrap') ||
                target.closest('.ant-modal-content')) {
                return;
            }

            if (cardRef.current && !cardRef.current.contains(target)) {
                onClose();
            }
        };

        // Add event listener with a small delay to prevent immediate closing
        const timeoutId = setTimeout(() => {
            document.addEventListener("mousedown", handleClickOutside);
        }, 0);

        return () => {
            clearTimeout(timeoutId);
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen, onClose, editingJobID]);

    if (!props.isOpen) {
        return null;
    }

    return (
        <>
            {/* Overlay */}
            <div
                onClick={props.onClose}
                style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    zIndex: 2000,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                {/* Schedule Card */}
                <div
                    ref={cardRef}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        backgroundColor: darkMode ? "#2d2d2d" : "white",
                        borderRadius: "8px",
                        boxShadow: darkMode ? "0 4px 12px rgba(0, 0, 0, 0.5)" : "0 4px 12px rgba(0, 0, 0, 0.15)",
                        width: "90vw",
                        height: "90vh",
                        position: "relative",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    {/* Header with close button */}
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "16px 20px",
                            borderBottom: darkMode ? "1px solid #404040" : "1px solid #E9E9E9",
                        }}
                    >
                        <h2 style={{ margin: 0, fontSize: "1.5em", fontWeight: "600", color: darkMode ? "#ffffff" : "#333" }}>
                            Schedule
                        </h2>
                        <ConditionalTooltip title="Close schedule">
                            <button
                                onClick={props.onClose}
                                style={{
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    padding: "4px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: darkMode ? "#ffffff" : "#666",
                                    fontSize: "18px",
                                }}
                            >
                                <CloseOutlined />
                            </button>
                        </ConditionalTooltip>
                    </div>

                    {/* Content area */}
                    <div
                        style={{
                            padding: "20px",
                            flex: 1,
                            overflow: "auto",
                        }}
                    >
                        {scheduledJobs.length === 0 ? (
                            <div style={{
                                textAlign: "center",
                                padding: "40px 20px",
                                color: darkMode ? "#888888" : "#666666"
                            }}>
                                No scheduled jobs
                            </div>
                        ) : (
                            <table style={{
                                width: "100%",
                                borderCollapse: "collapse",
                                fontSize: "14px"
                            }}>
                                <thead>
                                    <tr style={{
                                        borderBottom: darkMode ? "1px solid #404040" : "1px solid #E9E9E9"
                                    }}>
                                        {props.mode === 'dashboard' ? (
                                            <th style={{
                                                textAlign: "left",
                                                padding: "12px",
                                                fontWeight: "600",
                                                color: darkMode ? "#ffffff" : "#333",
                                                borderBottom: darkMode ? "1px solid #404040" : "1px solid #E9E9E9"
                                            }}>
                                                Dashboard Name
                                            </th>
                                        ) : (
                                            <>
                                                <th style={{
                                                    textAlign: "left",
                                                    padding: "12px",
                                                    fontWeight: "600",
                                                    color: darkMode ? "#ffffff" : "#333",
                                                    borderBottom: darkMode ? "1px solid #404040" : "1px solid #E9E9E9"
                                                }}>
                                                    Stream Name
                                                </th>
                                                <th style={{
                                                    textAlign: "left",
                                                    padding: "12px",
                                                    fontWeight: "600",
                                                    color: darkMode ? "#ffffff" : "#333",
                                                    borderBottom: darkMode ? "1px solid #404040" : "1px solid #E9E9E9"
                                                }}>
                                                    Request
                                                </th>
                                            </>
                                        )}
                                        <th style={{
                                            textAlign: "left",
                                            padding: "12px",
                                            fontWeight: "600",
                                            color: darkMode ? "#ffffff" : "#333",
                                            borderBottom: darkMode ? "1px solid #404040" : "1px solid #E9E9E9"
                                        }}>
                                            Frequency
                                        </th>
                                        <th style={{
                                            textAlign: "left",
                                            padding: "12px",
                                            fontWeight: "600",
                                            color: darkMode ? "#ffffff" : "#333",
                                            borderBottom: darkMode ? "1px solid #404040" : "1px solid #E9E9E9"
                                        }}>
                                            Next Run
                                        </th>
                                        <th style={{
                                            textAlign: "center",
                                            padding: "12px",
                                            width: "80px",
                                            borderBottom: darkMode ? "1px solid #404040" : "1px solid #E9E9E9"
                                        }}>
                                            {/* No header */}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {scheduledJobs.map((job) => {
                                        const scheduleTime = job.scheduleTime ? dayjs(job.scheduleTime) : null;
                                        const scheduleType = job.scheduleType || "";

                                        return (
                                            <tr
                                                key={job.jobID}
                                                style={{
                                                    borderBottom: darkMode ? "1px solid #404040" : "1px solid #E9E9E9"
                                                }}
                                            >
                                                {props.mode === 'dashboard' ? (
                                                    <td style={{
                                                        padding: "12px",
                                                        color: darkMode ? "#ffffff" : "#333"
                                                    }}>
                                                        <span
                                                            onClick={() => {
                                                                const jobToSelect = model.jobs.find(j => j.jobID === job.jobID);
                                                                if (jobToSelect) {
                                                                    model.selectedJob = jobToSelect;
                                                                    props.onClose();
                                                                }
                                                            }}
                                                            style={{
                                                                color: darkMode ? "#4A9EFF" : "#1890ff",
                                                                cursor: "pointer",
                                                                textDecoration: "underline",
                                                                textDecorationColor: darkMode ? "#4A9EFF" : "#1890ff"
                                                            }}
                                                            onMouseEnter={(e) => {
                                                                e.currentTarget.style.textDecoration = "underline";
                                                                e.currentTarget.style.opacity = "0.8";
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                e.currentTarget.style.textDecoration = "underline";
                                                                e.currentTarget.style.opacity = "1";
                                                            }}
                                                        >
                                                            {job.alias}
                                                        </span>
                                                    </td>
                                                ) : (
                                                    <>
                                                        <td style={{
                                                            padding: "12px",
                                                            color: darkMode ? "#ffffff" : "#333"
                                                        }}>
                                                            {getRootParentJobAlias(job)}
                                                        </td>
                                                        <td style={{
                                                            padding: "12px",
                                                            color: darkMode ? "#ffffff" : "#333"
                                                        }}>
                                                            <span
                                                                onClick={() => {
                                                                    const jobToSelect = model.jobs.find(j => j.jobID === job.jobID);
                                                                    if (jobToSelect) {
                                                                        model.selectedJob = jobToSelect;
                                                                        props.onClose();
                                                                    }
                                                                }}
                                                                style={{
                                                                    color: darkMode ? "#4A9EFF" : "#1890ff",
                                                                    cursor: "pointer",
                                                                    textDecoration: "underline",
                                                                    textDecorationColor: darkMode ? "#4A9EFF" : "#1890ff"
                                                                }}
                                                                onMouseEnter={(e) => {
                                                                    e.currentTarget.style.textDecoration = "underline";
                                                                    e.currentTarget.style.opacity = "0.8";
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    e.currentTarget.style.textDecoration = "underline";
                                                                    e.currentTarget.style.opacity = "1";
                                                                }}
                                                            >
                                                                {job.question}
                                                            </span>
                                                        </td>
                                                    </>
                                                )}
                                                <td style={{
                                                    padding: "12px",
                                                    color: darkMode ? "#ffffff" : "#333",
                                                    textTransform: "capitalize"
                                                }}>
                                                    {scheduleType}
                                                </td>
                                                <td style={{
                                                    padding: "12px",
                                                    color: darkMode ? "#ffffff" : "#333"
                                                }}>
                                                    {scheduleTime ? scheduleTime.format("YYYY-MM-DD h:mm A") : "—"}
                                                </td>
                                                <td style={{
                                                    padding: "12px",
                                                    textAlign: "center"
                                                }}>
                                                    <ConditionalTooltip title="Edit schedule">
                                                        <button
                                                            onClick={() => setEditingJobID(job.jobID)}
                                                            style={{
                                                                background: themeColors.primaryColor,
                                                                border: `1px solid ${themeColors.primaryColor}`,
                                                                borderRadius: "4px",
                                                                padding: "4px 12px",
                                                                cursor: "pointer",
                                                                color: "#ffffff",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: "4px",
                                                                fontSize: "13px",
                                                                transition: "all 0.2s",
                                                                fontWeight: "400"
                                                            }}
                                                            onMouseEnter={(e) => {
                                                                e.currentTarget.style.background = themeColors.primaryColorHover;
                                                                e.currentTarget.style.borderColor = themeColors.primaryColorHover;
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                e.currentTarget.style.background = themeColors.primaryColor;
                                                                e.currentTarget.style.borderColor = themeColors.primaryColor;
                                                            }}
                                                        >
                                                            Edit
                                                        </button>
                                                    </ConditionalTooltip>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>
            {/* Schedule Edit Modal */}
            {editingJobID && (() => {
                const editingJob = scheduledJobs.find(job => job.jobID === editingJobID);
                return editingJob ? (
                    <ScheduleRegenModalView
                        isOpen={true}
                        onClose={() => setEditingJobID(null)}
                        initialScheduleTime={editingJob.scheduleTime}
                        initialScheduleType={editingJob.scheduleType}
                        onSchedule={async (schedule: ScheduleData) => {
                            try {
                                if (schedule.clearSchedule) {
                                    await model.jobService.scheduleJob(editingJobID, null, null);
                                    notification.success({
                                        message: 'Schedule stopped',
                                        description: 'The schedule has been cleared.',
                                        placement: 'bottomLeft',
                                        duration: 3
                                    });
                                } else {
                                    const scheduleTime = schedule.date.toISOString();
                                    const scheduleType = schedule.frequency;

                                    await model.jobService.scheduleJob(editingJobID, scheduleTime, scheduleType);

                                    notification.success({
                                        message: 'Schedule updated',
                                        description: `Regeneration scheduled for ${schedule.date.format('YYYY-MM-DD h:mm A')} (${schedule.frequency}).`,
                                        placement: 'bottomLeft',
                                        duration: 3
                                    });
                                }
                                await model.reloadJobs();
                                setEditingJobID(null);
                            } catch (error) {
                                notification.error({
                                    message: 'Failed to update schedule',
                                    description: error instanceof Error ? error.message : 'Please try again.',
                                    placement: 'bottomLeft',
                                    duration: 3
                                });
                                throw error;
                            }
                        }}
                        darkMode={darkMode}
                    />
                ) : null;
            })()}
        </>
    );
}

